import { useEffect } from "react";
import { useTranslate } from "ra-core";
import { useFormContext, useWatch } from "react-hook-form";

import { AutocompleteInput } from "@/components/admin/autocomplete-input";
import { BooleanInput } from "@/components/admin/boolean-input";
import { DateInput } from "@/components/admin/date-input";
import { NumberInput } from "@/components/admin/number-input";
import { ReferenceInput } from "@/components/admin/reference-input";
import { SelectInput } from "@/components/admin/select-input";
import { TextInput } from "@/components/admin/text-input";
import { getSupabaseClient } from "@/components/atomic-crm/providers/supabase/supabase";
import { contactOptionText } from "@/components/atomic-crm/misc/ContactOption";
import { DuplicateWarning } from "./DuplicateWarning";
import {
  channelChoices,
  companyFieldsFor,
  monthChoices,
  orgTypeChoices,
  originChoices,
  paymentMethodChoices,
  priorityChoices,
} from "./choices";

const label = (field: string) => `tenax.people.fields.${field}`;

// A shop's minimum order starts from the general value in app_settings
// (D-031): a starting value the team can change, never a block.
const useShopMinOrderDefault = (isShop: boolean) => {
  const { setValue, getValues } = useFormContext();
  useEffect(() => {
    if (!isShop || getValues("min_order_amount") != null) return;
    getSupabaseClient()
      .from("app_settings")
      .select("value")
      .eq("key", "shop_min_order_default")
      .maybeSingle()
      .then(({ data }) => {
        if (data?.value != null && getValues("min_order_amount") == null) {
          setValue("min_order_amount", Number(data.value), {
            shouldDirty: true,
          });
        }
      });
  }, [isShop, getValues, setValue]);
};

// "Dati Tenax" of an organization (task 1.5, D-033): fields by type.
export const TenaxCompanyInputs = () => {
  const translate = useTranslate();
  const orgType = useWatch({ name: "org_type" }) as string | undefined;
  const fields = companyFieldsFor(orgType);
  useShopMinOrderDefault(fields.shop);

  return (
    <section className="flex flex-col gap-4">
      <h6 className="text-lg font-semibold">
        {translate("tenax.people.section")}
      </h6>
      <DuplicateWarning kind="company" />
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <SelectInput
          source="org_type"
          label={label("org_type")}
          choices={orgTypeChoices}
          defaultValue="other"
          helperText={false}
        />
        <SelectInput
          source="priority"
          label={label("priority")}
          choices={priorityChoices}
          helperText={false}
        />
        <TextInput source="region" label={label("region")} helperText={false} />
        <TextInput
          source="fic_client_id"
          label={label("fic_client_id")}
          helperText={false}
        />
        <SelectInput
          source="origin"
          label={label("origin")}
          choices={originChoices}
          helperText={false}
        />
        <SelectInput
          source="channel"
          label={label("channel")}
          choices={channelChoices}
          helperText={false}
        />

        {fields.club && (
          <>
            <NumberInput
              source="courts_count"
              label={label("courts_count")}
              helperText={false}
            />
            <TextInput
              source="group_name"
              label={label("group_name")}
              helperText={false}
            />
            <ReferenceInput
              source="introduced_by_contact_id"
              reference="contacts_summary"
            >
              <AutocompleteInput
                label={label("introduced_by_contact_id")}
                optionText={contactOptionText}
                helperText={false}
              />
            </ReferenceInput>
          </>
        )}

        {fields.shop && (
          <>
            <NumberInput
              source="min_order_amount"
              label={label("min_order_amount")}
              helperText={false}
            />
            <SelectInput
              source="payment_method"
              label={label("payment_method")}
              choices={paymentMethodChoices}
              helperText={false}
            />
            <NumberInput
              source="payment_discount_pct"
              label={label("payment_discount_pct")}
              helperText={false}
            />
            <BooleanInput
              source="consignment"
              label={label("consignment")}
              helperText={false}
            />
            <DateInput
              source="unsold_exchange_used_at"
              label={label("unsold_exchange_used_at")}
              helperText={translate(label("unsold_exchange_hint"))}
            />
          </>
        )}

        {fields.foreignRetailer && (
          <>
            <TextInput
              source="language"
              label={label("language")}
              helperText={false}
            />
            <TextInput
              source="country_code"
              label={label("country_code")}
              helperText={false}
            />
          </>
        )}

        {fields.corporateClient && (
          <SelectInput
            source="recontact_month"
            label={label("recontact_month")}
            choices={monthChoices}
            translateChoice={false}
            helperText={false}
          />
        )}
      </div>
    </section>
  );
};
